import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { registerApplication, start } from 'single-spa';
import singleSpaReact from 'single-spa-react';
import { autoinject } from 'aurelia-dependency-injection';
import { Container } from '@symbiotic/green-state';
import { ErrorPage } from '@asuresoftware/asure.strata-react';

import { App } from './App';
import { rootComponentBuilder } from '../framework/reactHelpers';
import { LocalhostHandler } from './LocalhostHandler';
import { IAccountData, ITenantData } from '../framework/production';
import { AccountService } from '../support/AccountService';
import { TenantService } from '../support/TenantService';
import { NavService } from '../support/NavService';
import { PortalFramework, PortalFrameworkBuilder } from '../framework/PortalFramework';

export class ErrorPageConfig {
  showDeveloperMessage = false;
}

@autoinject
export class PortalApp {
  constructor(
    private container: Container,
    private errorPageConfig: ErrorPageConfig,
    private localhostHandler: LocalhostHandler,
    private accountService: AccountService,
    private tenantService: TenantService,
    private navService: NavService
  ) { }

  async start(pluginLoader: () => Promise<void>) {
    const parcel = singleSpaReact({
      React,
      ReactDOM,
      rootComponent: rootComponentBuilder(App),
    });

    try {
      document.title = `AsureCloud`;

      // if localhost, redirect to defaultSubDomain.localhost
      await this.localhostHandler.checkForLocalhost();

      // Don't attempt to do login in parallel with other stuff below because if something blows up (like loading plugins),
      // the error handler will clear the access token while the login is redirecting, causing an infinite redirect loop.
      // It's fine to wait for login because there are only three possibilities:
      // (1) There's a token in session storage, in which case it will be synchronous and immediate
      // (2) We're redirecting to Heisenberg, in which case we want to stop all processing
      // (3) We're reloading the page, in which case we want to stop all processing
      const securityContext = await this.accountService.login();

      const account = securityContext.principal;
      this.container.registerInstance(IAccountData, account);

      // Start loading the nav, but don't block
      const navLoadedPromise = this.accountService.tryOrLogin(() => this.navService.mount());

      const tenant = await this.accountService.tryOrLogin(() => this.tenantService.getTenant(account.tenantId));
      this.container.registerInstance(ITenantData, tenant);

      const domElement = document.createElement('div');

      domElement.style['padding-top'] = '75px';

      document.body.appendChild(domElement);
      document.title = `AsureCloud | ${tenant.name}`;

      const { company } = await navLoadedPromise;

      this.container.registerInstance(PortalFramework, this.container.get(PortalFrameworkBuilder).build({
        securityContext,
        tenant,
        company
      }));

      // wait to load plugins until after framework instance is in container
      // so plugin developer can use authenticated user etc
      await pluginLoader();

      registerApplication(
        'Portal Nav and Dashboard',
        () => Promise.resolve(parcel),
        () => Promise.resolve(true),
        {
          domElement,
          container: this.container,
        }
      );

      start();

      await navLoadedPromise;
    } catch (err) {
      console.log(err);

      // if it's a bad subdomain, then reload makes no sense and we can't link to login
      const links = err.code === 'invalid-subdomain' ? undefined : [
        { label: 'Reload Page', callback: () => window.location.reload() },
        { label: 'Login', callback: () => this.accountService.logout() }
      ];

      const { showDeveloperMessage } = this.errorPageConfig;

      const errElement = document.createElement('div');
      document.body.appendChild(errElement);
      ReactDOM.render(<ErrorPage error={err} helpfulLinks={links} showDeveloperMessage={showDeveloperMessage}  />, errElement);

    } finally {
      (window as any).loadingPage.doneLoading();
    }
  }
}



// WEBPACK FOOTER //
// ./src/portal-react-app/PortalApp.tsx